import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText,
  Edit,
  Save,
  X,
  Eye,
  Calendar,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { updatePageContent } from '../../utils/contentManager';

const PageManagement = () => {
  const [selectedPage, setSelectedPage] = useState('home');
  const [pageData, setPageData] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({});
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const pages = [
    { id: 'home', name: 'Home Page', icon: FileText },
    { id: 'services', name: 'Services Page', icon: FileText },
    { id: 'about', name: 'About Page', icon: FileText },
    { id: 'caseStudies', name: 'Case Studies', icon: FileText },
    { id: 'blog', name: 'Blog Page', icon: FileText },
    { id: 'contact', name: 'Contact Page', icon: FileText }
  ];

  const pageFields = {
    home: [
      { key: 'heroTitle', label: 'Hero Title', type: 'text' },
      { key: 'heroSubtitle', label: 'Hero Subtitle', type: 'textarea' },
      { key: 'ctaText', label: 'Hero CTA Button Text', type: 'text' },
      { key: 'featuresTitle', label: 'Features Section Title', type: 'text' },
      { key: 'featuresSubtitle', label: 'Features Section Subtitle', type: 'textarea' },
      { key: 'statsTitle', label: 'Stats Section Title', type: 'text' },
      { key: 'statsSubtitle', label: 'Stats Section Subtitle', type: 'textarea' },
      { key: 'testimonialsTitle', label: 'Testimonials Section Title', type: 'text' },
      { key: 'testimonialsSubtitle', label: 'Testimonials Section Subtitle', type: 'textarea' },
      { key: 'ctaTitle', label: 'Final CTA Section Title', type: 'text' },
      { key: 'ctaSubtitle', label: 'Final CTA Section Subtitle', type: 'textarea' },
      { key: 'ctaButtonText', label: 'Final CTA Button Text', type: 'text' }
    ],
    services: [
      { key: 'title', label: 'Page Title', type: 'text' },
      { key: 'subtitle', label: 'Page Subtitle', type: 'textarea' },
      { key: 'processTitle', label: 'Process Section Title', type: 'text' },
      { key: 'processSubtitle', label: 'Process Section Subtitle', type: 'textarea' },
      { key: 'additionalTitle', label: 'Additional Services Title', type: 'text' },
      { key: 'additionalSubtitle', label: 'Additional Services Subtitle', type: 'textarea' },
      { key: 'ctaTitle', label: 'CTA Section Title', type: 'text' },
      { key: 'ctaSubtitle', label: 'CTA Section Subtitle', type: 'textarea' },
      { key: 'ctaButtonText', label: 'CTA Button Text', type: 'text' }
    ],
    about: [
      { key: 'title', label: 'Page Title', type: 'text' },
      { key: 'subtitle', label: 'Page Subtitle', type: 'textarea' },
      { key: 'missionTitle', label: 'Mission Section Title', type: 'text' },
      { key: 'mission', label: 'Mission Statement', type: 'textarea' },
      { key: 'missionDescription', label: 'Mission Description', type: 'textarea' },
      { key: 'visionTitle', label: 'Vision Section Title', type: 'text' },
      { key: 'vision', label: 'Vision Statement', type: 'textarea' },
      { key: 'valuesTitle', label: 'Values Section Title', type: 'text' },
      { key: 'valuesSubtitle', label: 'Values Section Subtitle', type: 'textarea' },
      { key: 'teamTitle', label: 'Team Section Title', type: 'text' },
      { key: 'teamSubtitle', label: 'Team Section Subtitle', type: 'textarea' },
      { key: 'journeyTitle', label: 'Journey Section Title', type: 'text' },
      { key: 'journeySubtitle', label: 'Journey Section Subtitle', type: 'textarea' },
      { key: 'ctaTitle', label: 'CTA Section Title', type: 'text' },
      { key: 'ctaSubtitle', label: 'CTA Section Subtitle', type: 'textarea' },
      { key: 'ctaButtonText', label: 'CTA Button Text', type: 'text' }
    ],
    caseStudies: [
      { key: 'title', label: 'Page Title', type: 'text' },
      { key: 'subtitle', label: 'Page Subtitle', type: 'textarea' },
      { key: 'impactTitle', label: 'Industry Impact Title', type: 'text' },
      { key: 'impactSubtitle', label: 'Industry Impact Subtitle', type: 'textarea' },
      { key: 'storiesTitle', label: 'Success Stories Title', type: 'text' },
      { key: 'storiesSubtitle', label: 'Success Stories Subtitle', type: 'textarea' },
      { key: 'frameworkTitle', label: 'Framework Section Title', type: 'text' },
      { key: 'frameworkSubtitle', label: 'Framework Section Subtitle', type: 'textarea' },
      { key: 'ctaTitle', label: 'CTA Section Title', type: 'text' },
      { key: 'ctaSubtitle', label: 'CTA Section Subtitle', type: 'textarea' },
      { key: 'ctaButtonText', label: 'CTA Button Text', type: 'text' }
    ],
    blog: [
      { key: 'title', label: 'Page Title', type: 'text' },
      { key: 'subtitle', label: 'Page Subtitle', type: 'textarea' },
      { key: 'featuredTitle', label: 'Featured Article Section Title', type: 'text' },
      { key: 'newsletterTitle', label: 'Newsletter Section Title', type: 'text' },
      { key: 'newsletterSubtitle', label: 'Newsletter Section Subtitle', type: 'textarea' },
      { key: 'newsletterButtonText', label: 'Newsletter Button Text', type: 'text' },
      { key: 'newsletterDisclaimer', label: 'Newsletter Disclaimer', type: 'textarea' }
    ],
    contact: [
      { key: 'title', label: 'Page Title', type: 'text' },
      { key: 'subtitle', label: 'Page Subtitle', type: 'textarea' },
      { key: 'contactTitle', label: 'Contact Section Title', type: 'text' },
      { key: 'contactSubtitle', label: 'Contact Section Subtitle', type: 'textarea' },
      { key: 'formTitle', label: 'Contact Form Title', type: 'text' },
      { key: 'formSubtitle', label: 'Contact Form Subtitle', type: 'textarea' },
      { key: 'whyChooseTitle', label: 'Why Choose Section Title', type: 'text' },
      { key: 'nextStepsTitle', label: 'Next Steps Section Title', type: 'text' }
    ]
  };

  useEffect(() => {
    loadPageData(selectedPage);
  }, [selectedPage]);

  const loadPageData = (page) => {
    try {
      const pagesData = JSON.parse(localStorage.getItem('oha_pages_data') || '{}');
      const data = pagesData[page] || {};
      setPageData(data);
      setFormData(data);
    } catch (error) {
      console.error('Error loading page data:', error);
      setPageData({});
      setFormData({});
    }
  };

  const handleEdit = (field) => {
    setEditingField(field);
    setError('');
    setSuccess('');
  };

  const handleSave = (field) => {
    try {
      const updatedContent = { [field.key]: formData[field.key] };
      const result = updatePageContent(selectedPage, updatedContent);

      if (result) {
        setPageData(prev => ({ ...prev, ...updatedContent }));
        setEditingField(null);
        setSuccess(`${field.label} updated successfully`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to update page data');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to update page data');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleCancel = () => {
    setFormData(pageData);
    setEditingField(null);
  };

  const handleInputChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handlePreview = () => {
    const pageMap = {
      home: '/',
      services: '/services',
      about: '/about',
      caseStudies: '/case-studies',
      blog: '/blog',
      contact: '/contact'
    };
    window.open(pageMap[selectedPage] || '/', '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Page Management</h1>
          <p className="text-gray-600 mt-2">Edit website content and page details</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handlePreview}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center space-x-2"
        >
          <Eye className="w-5 h-5" />
          <span>Preview Website</span>
        </motion.button>
      </div>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3"
          >
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700">{error}</span>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3"
          >
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700">{success}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Page Selector */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Page</h3>
            <div className="space-y-2">
              {pages.map((page) => (
                <button
                  key={page.id}
                  onClick={() => setSelectedPage(page.id)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors duration-200 ${
                    selectedPage === page.id
                      ? 'bg-blue-100 text-blue-800 border border-blue-200'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <page.icon className="w-5 h-5 mr-3" />
                  {page.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Page Editor */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Edit {pages.find(p => p.id === selectedPage)?.name}
              </h3>
              {pageData.lastUpdated && (
                <div className="flex items-center text-sm text-gray-500">
                  <Calendar className="w-4 h-4 mr-2" />
                  Last updated: {new Date(pageData.lastUpdated).toLocaleDateString()}
                </div>
              )}
            </div>

            <div className="space-y-6 max-h-96 overflow-y-auto">
              {pageFields[selectedPage]?.map((field) => (
                <div key={field.key} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">
                      {field.label}
                    </label>
                    {editingField?.key === field.key ? (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleSave(field)}
                          className="text-green-600 hover:text-green-700"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleCancel}
                          className="text-gray-600 hover:text-gray-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEdit(field)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {editingField?.key === field.key ? (
                    field.type === 'textarea' ? (
                      <textarea
                        value={formData[field.key] || ''}
                        onChange={(e) => handleInputChange(field.key, e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none
                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                      />
                    ) : (
                      <input
                        type="text"
                        value={formData[field.key] || ''}
                        onChange={(e) => handleInputChange(field.key, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none
                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                      />
                    )
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-3 min-h-[60px] flex items-center">
                      <p className="text-gray-900 whitespace-pre-wrap">
                        {pageData[field.key] || (
                          <span className="text-gray-500 italic">No content set</span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              ))}

              {pageFields[selectedPage]?.length === 0 && (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No editable fields available for this page</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Page Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-800 mb-2">Page Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-700">
          <div>
            <span className="font-medium">Page:</span>{' '}
            {pages.find(p => p.id === selectedPage)?.name}
          </div>
          <div>
            <span className="font-medium">Last Updated:</span>{' '}
            {pageData.lastUpdated ? new Date(pageData.lastUpdated).toLocaleDateString() : 'Never'}
          </div>
          <div>
            <span className="font-medium">Fields Available:</span>{' '}
            {pageFields[selectedPage]?.length || 0}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageManagement;
